import React, { useEffect, useState } from 'react';
import axios from 'axios';
import config from '../config';
import { useTranslation } from 'react-i18next';

/** Decode leftover HTML entities (safety net for cached backend data) */
function cleanHtml(s) {
  if (!s) return s;
  return s.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"');
}

const countryNames = {
  'CZ': 'Czech Republic', 'CY': 'Cyprus', 'NL': 'Netherlands',
  'SK': 'Slovakia', 'DE': 'Germany', 'LU': 'Luxembourg',
  'UK': 'United Kingdom', 'AT': 'Austria',
};

/** Parse date-of-birth from node IDs like RC_LASTNAME_FIRSTNAME_1980-01-01 */
function parseDobFromId(id) {
  if (!id) return null;
  const match = id.match(/(\d{4}-\d{2}-\d{2})$/);
  return match ? match[1] : null;
}

/** Content component — manages its own fetch + navigation stack */
function PanelContent({ initialEntity, initialNeighbors, onClose, onFindPaths }) {
  const { t } = useTranslation();
  const [stack, setStack] = useState([{ entity: initialEntity, neighbors: initialNeighbors || [] }]);
  const [extraData, setExtraData] = useState(null);
  const [orDetail, setOrDetail] = useState(null);
  const [orLoading, setOrLoading] = useState(false);

  const current = stack[stack.length - 1];
  const entity = current.entity;
  const neighbors = current.neighbors;

  // Re-fetch whenever current entity changes
  useEffect(() => {
    setExtraData(null);
    setOrDetail(null);
    if (!entity) return;

    axios.get(`${config.API_BASE_URL}/api/entities/${entity.id}`)
      .then(r => setExtraData(r.data))
      .catch(() => {});

    const isNumeric = /^\d+$/.test(entity.id);
    if (entity.type === 'company' && isNumeric) {
      setOrLoading(true);
      axios.get(`${config.API_BASE_URL}/api/company-detail/${entity.id}`)
        .then(r => setOrDetail(r.data))
        .catch(() => {})
        .finally(() => setOrLoading(false));
    }
  }, [entity.id, entity.type]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateTo = (id, label, type) => {
    setStack(prev => [...prev, { entity: { id, label, type }, neighbors: [] }]);
  };

  const goBack = () => {
    if (stack.length > 1) setStack(prev => prev.slice(0, -1));
  };

  const isCompany = entity.type === 'company';
  const ico = entity.id;
  const dob = !isCompany ? parseDobFromId(ico) : null;

  const datumVzniku = extraData?.datum_vzniku || orDetail?.datum_vzniku || '';
  const zakladniKapital = extraData?.zakladni_kapital || orDetail?.zakladni_kapital || '';
  const predmetPodnikani = extraData?.predmet_podnikani?.length > 0
    ? extraData.predmet_podnikani
    : orDetail?.predmet_podnikani || [];
  const zpusobJednani = extraData?.zpusob_jednani || orDetail?.zpusob_jednani || '';
  const legalForm = orDetail?.legal_form || extraData?.ares?.legal_form || '';
  const orAddress = orDetail?.address || '';
  const country = entity.country || 'CZ';

  const neighborList = extraData?.neighbors || neighbors || [];

  const directors = [];
  const shareholders = [];
  const otherConnections = [];

  neighborList.forEach(n => {
    const rel = n.edgeType || n.relationship || 'connection';
    const relLower = rel.toLowerCase();
    const isInactive = n.edgeActive === false || n.active === false;
    const item = { ...n, relType: rel, isInactive };

    const isDirector =
      relLower === 'jednatel' ||
      relLower.includes('představenstvo') ||
      relLower.includes('představenstva') ||
      relLower.includes('správní rad') ||
      relLower.includes('správce') ||
      relLower.includes('prokur') ||
      relLower.includes('likvidátor') ||
      relLower.includes('revizor') ||
      relLower.includes('dozorčí') ||
      relLower.includes('ředitel') ||
      relLower.includes('vedoucí') ||
      relLower.includes('komplementář') ||
      relLower.includes('statutory');

    if (isDirector) {
      directors.push(item);
    } else if (relLower === 'společník' || relLower.includes('akcionář') || relLower.includes('člen')) {
      shareholders.push(item);
    } else {
      otherConnections.push(item);
    }
  });

  const isNumericIco = /^\d+$/.test(ico);
  const orDirectUrl = orDetail?.or_url;
  const orLinks = isCompany && isNumericIco ? {
    uplnyVypis: orDirectUrl || `https://or.justice.cz/ias/ui/rejstrik-$firma?p%3A%3Asubmit=x&ico=${ico}&nazev=&obec=&ulice=&oddil=&vlozka=`,
    sbirkaListin: orDetail?.subjekt_id
      ? `https://or.justice.cz/ias/ui/vypis-sl-firma?subjektId=${orDetail.subjekt_id}`
      : `https://or.justice.cz/ias/ui/rejstrik-$firma?p%3A%3Asubmit=x&ico=${ico}&nazev=&obec=&ulice=&oddil=&vlozka=`
  } : null;

  const renderConnectionItem = (n, i) => {
    const startDate = n.start_date || '';
    const endDate = n.end_date || '';
    const canNavigate = n.id && n.type;
    return (
      <div
        key={i}
        className={`connection-item ${n.isInactive ? 'inactive' : ''} ${canNavigate ? 'navigable' : ''}`}
        onClick={canNavigate ? () => navigateTo(n.id, n.label || n.name || n.id, n.type) : undefined}
        title={canNavigate ? `Open ${n.label || n.id}` : undefined}
      >
        <span className="connection-icon">
          {n.type === 'company' ? '\u{1F3E2}' : '\u{1F464}'}
        </span>
        <span className="connection-name">{n.label || n.name || n.id}</span>
        {n.share_pct && (
          <span className="connection-share">{n.share_pct}%</span>
        )}
        {n.share_vklad && !n.share_pct && (
          <span className="connection-share">{Number(n.share_vklad).toLocaleString('cs-CZ')} Kč</span>
        )}
        {n.relType && n.relType !== 'společník' && n.relType !== 'jednatel' && (
          <span className="connection-role">{n.relType}</span>
        )}
        {(startDate || endDate) && (
          <span className="connection-dates">
            {startDate && startDate.substring(0, 10)}
            {startDate && endDate && ' – '}
            {endDate && endDate.substring(0, 10)}
          </span>
        )}
        {n.isInactive && <span className="connection-inactive">{t('panel.historical_badge')}</span>}
        {canNavigate && <span className="connection-nav-arrow">›</span>}
      </div>
    );
  };

  return (
    <div className="entity-panel">
      {/* Header */}
      <div className="panel-header">
        <div className="panel-nav-row">
          {stack.length > 1 && (
            <button className="panel-back-btn" onClick={goBack} title={t('panel.back')}>
              {t('panel.back')}
            </button>
          )}
          {stack.length > 1 && (
            <span className="panel-breadcrumb">
              {stack[0].entity.label || stack[0].entity.id}
            </span>
          )}
          <button className="panel-close-btn" onClick={onClose} title={t('panel.close')}>×</button>
        </div>
        <div className="panel-title-row">
          <span className={`modal-type-badge ${entity.type}`}>
            {isCompany ? '\u{1F3E2}' : '\u{1F464}'}
          </span>
          <div>
            <h2 className="panel-name">{entity.label || entity.id}</h2>
            <span className="modal-type-label">{isCompany ? t('panel.company') : t('panel.person')}</span>
          </div>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="panel-body">

        {/* Basic details */}
        <div className="modal-details-grid">
          <div className="detail-item">
            <span className="detail-label">{isCompany ? 'IČO' : t('panel.id_label')}</span>
            <span className="detail-value mono">{ico}</span>
          </div>
          {dob && (
            <div className="detail-item">
              <span className="detail-label">{t('panel.dob')}</span>
              <span className="detail-value">{dob}</span>
            </div>
          )}
          {(entity.city || orAddress) && (
            <div className="detail-item">
              <span className="detail-label">Sídlo</span>
              <span className="detail-value">{orAddress || entity.city}</span>
            </div>
          )}
          <div className="detail-item">
            <span className="detail-label">{t('panel.country')}</span>
            <span className="detail-value">{countryNames[country] || country}</span>
          </div>
          {legalForm && (
            <div className="detail-item">
              <span className="detail-label">Právní forma</span>
              <span className="detail-value">{legalForm}</span>
            </div>
          )}
          {datumVzniku && (
            <div className="detail-item">
              <span className="detail-label">Datum vzniku</span>
              <span className="detail-value">{datumVzniku}</span>
            </div>
          )}
          {zakladniKapital && (
            <div className="detail-item">
              <span className="detail-label">Základní kapitál</span>
              <span className="detail-value">{zakladniKapital}</span>
            </div>
          )}
          {entity.insolvent && (
            <div className="detail-item warning">
              <span className="detail-label">{t('panel.status')}</span>
              <span className="detail-value insolvent-status">{t('panel.insolvent_status')}</span>
            </div>
          )}
        </div>

        {/* Business activities */}
        {predmetPodnikani.length > 0 && (
          <div className="modal-extra-section">
            <h3>Předmět podnikání</h3>
            <ul className="modal-list">
              {predmetPodnikani.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </div>
        )}

        {/* Manner of acting */}
        {zpusobJednani && (
          <div className="modal-extra-section">
            <h3>Způsob jednání</h3>
            <p className="modal-text">{zpusobJednani}</p>
          </div>
        )}

        {/* ARES */}
        {extraData?.ares && (
          <div className="modal-extra-section">
            <h3>ARES</h3>
            <div className="modal-details-grid" style={{ padding: 0 }}>
              {extraData.ares.capital && (
                <div className="detail-item">
                  <span className="detail-label">Kapitál</span>
                  <span className="detail-value mono">{extraData.ares.capital}</span>
                </div>
              )}
              {extraData.ares.employees && (
                <div className="detail-item">
                  <span className="detail-label">Zaměstnanci</span>
                  <span className="detail-value">{extraData.ares.employees}</span>
                </div>
              )}
            </div>
            {extraData.ares.nace_codes?.length > 0 && (
              <ul className="modal-list">
                {extraData.ares.nace_codes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            )}
          </div>
        )}

        {/* ISIR */}
        {extraData?.isir_proceedings > 0 && (
          <div className="modal-extra-section">
            <div className="detail-item warning">
              <span className="detail-label">ISIR</span>
              <span className="detail-value insolvent-status">
                {extraData.isir_proceedings} insolvenční řízení
              </span>
            </div>
          </div>
        )}

        {orLoading && (
          <div className="panel-or-loading">
            <div className="panel-or-spinner" />
            <span>{t('panel.or_loading')}</span>
          </div>
        )}

        {/* Historical company names */}
        {extraData?.historical_names?.length > 0 && (
          <div className="modal-extra-section">
            <h3>{t('panel.history')}</h3>
            <ul className="modal-list">
              {extraData.historical_names.map((h, i) => (
                <li key={i}>
                  {h.name}
                  {(h.from || h.to) && (
                    <span className="connection-dates">
                      {' '}({h.from && h.from.substring(0, 10)}{h.from && h.to && ' – '}{h.to && h.to.substring(0, 10)})
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* OR links */}
        {orLinks && (
          <div className="modal-or-links">
            <a href={orLinks.uplnyVypis} target="_blank" rel="noopener noreferrer" className="or-link-btn">
              Zobrazit v OR →
            </a>
            <a href={orLinks.sbirkaListin} target="_blank" rel="noopener noreferrer" className="or-link-btn secondary">
              Sbírka listin
            </a>
          </div>
        )}

        {/* Generic OR sections — show all remaining sections not covered above */}
        {orDetail?.raw_sections?.length > 0 && (() => {
          const SHOWN_HEADINGS = new Set([
            'Datum vzniku a zápisu', 'Datum vzniku', 'Právní forma', 'Sídlo',
            'Základní kapitál', 'Způsob jednání', 'Předmět podnikání',
            'Předmět činnosti', 'Statutární orgán', 'Společníci',
            'Jediný akcionář', 'Akcionáři',
          ]);
          const extra = orDetail.raw_sections.filter(
            s => !SHOWN_HEADINGS.has(s.heading)
          );
          if (extra.length === 0) return null;
          return (
            <div>
              {extra.map((section, i) => (
                <div key={i} className="modal-extra-section">
                  <h3>{section.heading}</h3>
                  <ul className="modal-list">
                    {section.items.map((item, j) => {
                      const text = cleanHtml(item);
                      const isHistorical = text.toLowerCase().includes('vymazáno') || text.toLowerCase().includes('vymazano');
                      return <li key={j} className={isHistorical ? 'section-item-historical' : ''}>{text}</li>;
                    })}
                  </ul>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Connections */}
        {directors.length > 0 && (
          <div className="modal-connections">
            <h3>👤 Statutární orgán ({directors.length})</h3>
            {directors.map(renderConnectionItem)}
          </div>
        )}

        {shareholders.length > 0 && (
          <div className="modal-connections">
            <h3>💰 Společníci ({shareholders.length})</h3>
            {shareholders.map(renderConnectionItem)}
          </div>
        )}

        {otherConnections.length > 0 && (
          <div className="modal-connections">
            <h3>{t('panel.connections_heading')} ({otherConnections.length})</h3>
            {otherConnections.map(renderConnectionItem)}
          </div>
        )}

        {directors.length === 0 && shareholders.length === 0 && otherConnections.length === 0 && (
          <div className="modal-connections">
            <p className="no-connections">{t('panel.no_connections')}</p>
          </div>
        )}

        {/* Find paths action */}
        <button className="modal-action-btn" style={{ margin: '16px 0' }} onClick={() => onFindPaths(entity)}>
          {t('panel.find_paths')}
        </button>
      </div>
    </div>
  );
}

function EntityPanel({ entity, neighbors, onClose, onFindPaths }) {
  if (!entity) return null;
  return (
    <PanelContent
      key={entity.id}
      initialEntity={entity}
      initialNeighbors={neighbors}
      onClose={onClose}
      onFindPaths={onFindPaths}
    />
  );
}

export default EntityPanel;
